import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';

import { PARTNER_LEASING_REFERRAL_AGREEMENT_2023 } from 'common/partnerConstants';

import sendDataToMobileApp, {
  SR_MOBILE_APP_ACTION_TYPE_OPEN_URL,
} from 'client/helpers/sendDataToMobileApp';

import { formatPrice } from 'common/formatters';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import useBranch from 'client/hooks/useBranch';

import {
  BUTTON_THEME_GREEN_ADOBE,
  BUTTON_THEME_GREY_ADOBE,
} from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_TRANSPARENT_BLUE_BORDER,
} from 'client/components/SrButton/SrButton';
import SrTextGreycliff from 'client/components/SrTextGreycliff';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

const formFields = [
  {
    id: 'referralAgreement2023SignedDate',
    type: 'simple_checkbox',
    properties: {
      children: (
        <SrBox textAlign="left" marginTop="-6px">
          <SrTextGreycliff weight="700" lineHeight="22px" color="greyMonochrome">
            By clicking this checkbox I am electronically signing and agreeing to the 2023 Referral
            Agreement
          </SrTextGreycliff>
        </SrBox>
      ),
      className: 'adobeCheckboxSquare16',
    },
    validations: ['required', 'legal'],
  },
];

const SrPartnerReferralAgreement2023ModalContent = ({ partner, formikBag }) => {
  const { isMobileApp, appUrl } = useBranch({
    isMobileApp: ['mobileApp', 'isMobileApp'],
    appUrl: ['env', 'APP_URL'],
  });

  return (
    <SrBox>
      <SrTextGreycliff size="18" weight="800" lineHeight="28px">
        Agreement Terms
      </SrTextGreycliff>
      <br />
      <SrTextGreycliff size="18" weight="600" lineHeight="28px">
        {partner.billingFees?.leaseFeePercentage < 0.65 && (
          <>
            Leasing fee = 65%
            <br />
          </>
        )}
        Withdrawal fee = {formatPrice(partner.billingFees?.withdrawalFee)}
      </SrTextGreycliff>
      <SrBox margin="20px 0">
        <SrButton
          theme={SR_BUTTON_THEME_ADOBE_TRANSPARENT_BLUE_BORDER}
          onClick={() => {
            if (isMobileApp) {
              sendDataToMobileApp({
                actionType: SR_MOBILE_APP_ACTION_TYPE_OPEN_URL,
                url: `${appUrl}/pdf-viewer?pdfUrl=${PARTNER_LEASING_REFERRAL_AGREEMENT_2023}`,
              });
            } else {
              global.open(
                `${appUrl}/pdf-viewer?pdfUrl=${PARTNER_LEASING_REFERRAL_AGREEMENT_2023}`,
                '_blank',
              );
            }
          }}
        >
          View full agreement
        </SrButton>
      </SrBox>
      <SrTextGreycliff size="18" weight="800" lineHeight="28px">
        Why do we make this change?
      </SrTextGreycliff>
      <br />
      <SrTextGreycliff size="18" weight="600" lineHeight="28px">
        Sunroom charges a withdrawal fee if a property owner decides to remove their listing after
        it has already been set up and listed online, which can sometimes result in unforeseen
        costs.
      </SrTextGreycliff>
      {formFields.map((formField) => (
        <SrFormikField
          key={formField.id}
          formField={formField}
          formikBag={formikBag}
          theme={SR_FIELD_THEME_ADOBE}
        />
      ))}
    </SrBox>
  );
};

const SrPartnerReferralAgreement2023Modal = ({ partner, onClose, onSubmit }) => {
  return (
    <Formik
      initialValues={createInitialValues({
        formFields: formFields,
      })}
      validate={createFormValidationFn(formFields)}
      onSubmit={onSubmit}
      render={(formikBag) => {
        return (
          <SrSimpleMessageModal
            innerStyle={{ padding: '12px' }}
            headline="New Referral Agreement"
            underline
            message={
              <SrPartnerReferralAgreement2023ModalContent partner={partner} formikBag={formikBag} />
            }
            multiButtons={[
              {
                buttonText: 'Continue',
                buttonTheme: BUTTON_THEME_GREEN_ADOBE,
                buttonDisabled: !formikBag.isValid,
                onButtonClick: formikBag.handleSubmit,
              },
              {
                buttonText: 'Close',
                buttonTheme: BUTTON_THEME_GREY_ADOBE,
                onButtonClick: onClose,
              },
            ]}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            maxWidth={375}
          />
        );
      }}
    />
  );
};

SrPartnerReferralAgreement2023Modal.propTypes = {
  partner: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

SrPartnerReferralAgreement2023Modal.displayName = 'SrPartnerReferralAgreement2023Modal';

export default SrPartnerReferralAgreement2023Modal;
